import React from "react";
import { ShieldCheck } from "lucide-react";

const sections = [
  {
    title: "1. Privacy Commitment",
    paragraphs: [
      "We are dedicated to providing a safe and secure online experience for all students, parents, applicants, and website visitors.",
      "Your personal information is treated with confidentiality and is never sold, rented, or shared with third parties for marketing purposes without your consent.",
    ],
  },
  {
    title: "2. Information We Collect",
    paragraphs: ["We may collect information including:"],
    list: [
      "Full Name",
      "Email Address",
      "Mobile Number",
      "City and State",
      "Course Preferences",
      "Educational Details",
      "Information submitted through enquiry, admission, scholarship, or contact forms",
      "Technical information such as browser type, IP address, device information, and website usage data",
    ],
  },
  {
    title: "3. How We Use Your Information",
    paragraphs: ["The information collected may be used to:"],
    list: [
      "Respond to your enquiries",
      "Process admission applications",
      "Provide counselling and course information",
      "Send admission updates and important notifications",
      "Share event invitations, newsletters, workshops, and promotional communications",
      "Improve our website and user experience",
      "Maintain internal records and administrative purposes",
    ],
  },
  {
    title: "4. Information Sharing",
    paragraphs: [
      "INSD Bhubaneswar does not sell, rent, or trade your personal information.",
      "Information may only be disclosed:",
    ],
    list: [
      "When required by applicable law",
      "To comply with legal proceedings or government requests",
      "To protect the rights, safety, or property of INSD Bhubaneswar, its students, employees, or the public",
    ],
  },
  {
    title: "5. Cookies",
    paragraphs: ["Our website may use cookies and similar technologies to:"],
    list: [
      "Improve website functionality",
      "Remember user preferences",
      "Analyse website traffic",
      "Enhance user experience",
    ],
    footer:
      "You may disable cookies through your browser settings; however, some website features may not function properly.",
  },
  {
    title: "6. Third-Party Services",
    paragraphs: [
      "Our website may contain links to third-party websites or integrate services such as:",
    ],
    list: [
      "Google Analytics",
      "Google Maps",
      "Social Media Platforms",
      "Payment Gateways",
      "Online Application Forms",
    ],
    footer:
      "These third-party services have their own privacy policies, and INSD Bhubaneswar is not responsible for their content or privacy practices.",
  },
  {
    title: "7. Data Security",
    paragraphs: [
      "We implement reasonable administrative, technical, and organizational measures to protect your personal information against unauthorized access, misuse, alteration, or disclosure.",
      "While we strive to safeguard your information, no method of internet transmission or electronic storage is completely secure. Therefore, we cannot guarantee absolute security.",
    ],
  },
  {
    title: "8. Your Rights",
    paragraphs: ["You may request to:"],
    list: [
      "Access your personal information",
      "Correct inaccurate information",
      "Update your details",
      "Request deletion of your personal information, subject to applicable legal or institutional requirements",
    ],
    footer:
      "Requests may be submitted using the contact details provided on our website.",
  },
  {
    title: "9. Consent",
    paragraphs: [
      "By accessing or using the INSD Bhubaneswar website, you acknowledge that you have read, understood, and agreed to this Privacy Policy.",
    ],
  },
  {
    title: "10. Policy Updates",
    paragraphs: [
      "INSD Bhubaneswar reserves the right to modify or update this Privacy Policy at any time without prior notice.",
      "The latest version will always be available on the official website, and continued use of the website constitutes acceptance of the updated policy.",
    ],
  },
];

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-primaryColor/10 text-primaryColor p-3 rounded-full">
            <ShieldCheck size={28} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-primaryTextColor">
            Privacy Policy
          </h1>
        </div>

        <p className="text-secondaryTextColor leading-relaxed mb-10">
          At International School of Design (INSD) Bhubaneswar, we respect your
          privacy and are committed to protecting the personal information you
          share with us. This Privacy Policy explains how we collect, use, and
          protect your information when you visit our website.
        </p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-100">
          {sections.map((section) => (
            <section key={section.title} className="p-6 sm:p-8">
              <h2 className="text-lg sm:text-xl font-bold text-primaryTextColor mb-3">
                {section.title}
              </h2>

              {section.paragraphs?.map((para, idx) => (
                <p
                  key={idx}
                  className="text-secondaryTextColor leading-relaxed mb-3 last:mb-0"
                >
                  {para}
                </p>
              ))}

              {section.list && (
                <ul className="mt-2 mb-3 space-y-2">
                  {section.list.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 text-secondaryTextColor"
                    >
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primaryColor flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.footer && (
                <p className="text-secondaryTextColor leading-relaxed">
                  {section.footer}
                </p>
              )}
            </section>
          ))}
        </div>

        <p className="text-xs text-gray-400 text-center mt-8">
          International School of Design (INSD), Bhubaneswar
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
